import { Badge, Divider, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect } from 'react';
import moment from "moment-timezone";
import { ShAvatar, ShChip } from "shared/SharedStyles/ShDataDisplay";
import { ShPaper } from "shared/SharedStyles/ShSurfaces";
import { ShDarkGreen } from 'shared/SharedStyles/styleConstants';
import { getAccInfo, getAccountPendingReviewApplicantCount, resetUpdateAccInfo } from 'store/slices/employer/settingsTs/acc-info-slice';
import { getCompanyInfo, getCompanyLogo } from 'store/slices/employer/settingsTs/company-info-slice';
import { ShTooltip } from "shared/SharedComponents/ShTooltip";
import { getInitialsFromName, stringToColor } from "shared/utils";
import { calculateAccountSetupProgress, calculateCompanySetupProgress } from "../DashboardConstants";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";

Sentry.captureException(new Error("Test error from App"));

export const DashboardEmployerWelcomeWidget = () => {
    const dispatch = useAppDispatch();
    const { firstName, middleName, lastName, accountAccess, accountLoginInfo, twoStepAuth } = useAppSelector((state) => state.auth.auth);
    const userInitials = getInitialsFromName(`${firstName} ${lastName}`, true, true);
    const getAvatarBackgroundColor = stringToColor(userInitials);
    const { accountInfo, countOfApplicantsToReview } = useAppSelector((state) => state.employer.employerSettings.accInfo);
    const userTimezone = accountInfo.timezone || moment.tz.guess();
    const currentHour = moment.tz(userTimezone).hour();
    const { userImage } = useAppSelector((state) => state.employer.employerSettings.accInfo);
    const { companyInfo, companyLogo } = useAppSelector(state => state.employer.employerSettings.companyInfo);
    const accountSetupProgress = calculateAccountSetupProgress(accountInfo, userImage);
    const companySetupProgress = calculateCompanySetupProgress(companyInfo, companyLogo);
    const navigate = useNavigate();

    useEffect(() => {
        if (!accountInfo.id) {
            dispatch(getAccInfo());
        }
        return () => {
            dispatch(resetUpdateAccInfo());
        }
    }, [accountInfo.id, dispatch]);

    useEffect(() => {
        if (!companyInfo.id) {
            dispatch(getCompanyInfo());
        }
    }, [companyInfo, dispatch]);

    useEffect(() => {
        dispatch(getAccountPendingReviewApplicantCount());
    }, [dispatch]);

    useEffect(() => {
        if (companyLogo?.length === 0) {
            dispatch(getCompanyLogo());
        }
    }, [companyLogo, dispatch]);

    const getGreetingMessage = () => {
        if (currentHour < 12) {
            return "Good Morning";
        } else if (currentHour < 18) {
            return "Good Afternoon";
        } else {
            return "Good Evening";
        }
    }

    return (
        <ShPaper variant="outlined" height="100%" borderRadius={15}>
            
            <Stack padding={1} direction={{ xs: 'column', sm: 'column', md: 'row', lg: 'row' }} justifyContent="space-between" alignContent="center">
                {/* Avatar and User Greeting */}
                <Stack flexDirection='row' alignItems='center' columnGap={2}>
                    <Badge
                        overlap="rectangular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        badgeContent={!twoStepAuth?.emailVerification && !twoStepAuth?.securityVerification &&
                            <ShTooltip title="Two-Step Authentication is not enabled. Your account/data may be vulnerable!" placement='bottom-start'>
                                <ErrorOutlineIcon color='warning' />
                            </ShTooltip>
                        }>
                        <ShAvatar className="dashboard-user-avatar" backgroundColor={getAvatarBackgroundColor} src={userImage}>
                            <Typography variant='h6'>
                                {userInitials.toUpperCase()}
                            </Typography>
                        </ShAvatar>
                    </Badge>
                    <Typography variant="body1">
                        {getGreetingMessage()}, {[firstName, middleName, lastName].filter(Boolean).join(' ')}
                    </Typography>

                    <ShChip size='small' borderRadius='25' textColor="#ffffff" bgColor={ShDarkGreen} label={accountAccess.role?.toUpperCase().replace(/_/g, ' ')}/>
                </Stack>
            </Stack>
            {/* Last Login */}
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' spacing={2} alignItems='center'>
                        {accountLoginInfo?.last_sign_in_at &&
                            <Typography padding={1} textAlign='left' variant="caption" sx={{ opacity: .7 }}>
                                Last login: {moment(accountLoginInfo?.last_sign_in_at)?.fromNow()}
                            </Typography>
                        }
                        <>
                            <Divider flexItem orientation="vertical" />
                            <ShChip
                                hideBoxShadow
                                label={`${countOfApplicantsToReview} ${countOfApplicantsToReview === 1 ? 'Applicant' : 'Applicants'} Assigned to You`}
                                onClick={() => navigate("/employer/applicants-to-review")} 
                                variant="outlined"
                                size="small"
                                customSize="xs"
                            />
                        </>
                    </Stack>
                    <Stack direction='row' spacing={3}>
                        {/* Account Setup Progress */}
                        {accountSetupProgress !== 100 &&
                            <Stack direction="column" spacing={1} padding={1}>
                                <Typography variant="caption">Account Setup Progress</Typography>
                                <LinearProgress variant="determinate" value={accountSetupProgress} />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="textSecondary">Completion: {Math.floor(accountSetupProgress)}%</Typography>
                                    <ShTooltip title="Finish Account Setup" placement='right-start'>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate('/employer/settings/account/account-information')}
                                            color="primary"
                                        >
                                            <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                    </ShTooltip>
                                </Stack>
                            </Stack>
                        }
                        {/* Company Setup Progress */}
                        {companySetupProgress !== 100 &&
                            <Stack direction="column" spacing={1} padding={1}>
                                <Typography variant="caption">Company Setup Progress</Typography>
                                <LinearProgress variant="determinate" value={companySetupProgress} />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="textSecondary">
                                        Completion: {Math.floor(companySetupProgress)}%
                                    </Typography>
                                    <ShTooltip title="Finish Company Setup" placement='right-start'>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate('/employer/settings/account/company-information')}
                                            color="primary"
                                        >
                                            <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                    </ShTooltip>
                                </Stack>
                            </Stack>
                        }
                    </Stack>
            </Stack>
          
        </ShPaper>
    );
};